import { VerticalContainer } from '@american-autonomy/ag-ui';

import LabeledToggle from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle';
import { VARIANT_SWITCH } from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle/constants';
import useApplicationDetailsTranslation from 'pages/ApplicationDetailsPage/hooks/useTranslation';

import useImperialHandlers from './hooks/useImperialHandlers';
import useTranslation from './hooks/useTranslation';

import PrimaryParamsSliders from './components/PrimaryParamsSliders';
import SectionEyebrow from './components/SectionEyebrow';
import {
    AUTO_OPTIMIZE_OFF_INDEX,
    AUTO_OPTIMIZE_ON_INDEX,
    AUTO_OPTIMIZE_ON_VALUE,
    AUTO_OPTIMIZE_OPTIONS,
    TEST_ID,
    TURN_TYPE_OPTIONS
} from './constants';
import type { FunctionComponent, Props, RoutePlanConfig, TurnType } from './types';

import styles from './styles.module.scss';

const PrimaryParamsSection: FunctionComponent<Props> = ({
    className = '',
    config,
    onConfigChange,
    testId = TEST_ID
}) => {
    const { PATH_ANGLE_HINT, PRIMARY_PARAMS_EYEBROW_SUBTITLE, PRIMARY_PARAMS_EYEBROW_TITLE } =
        useApplicationDetailsTranslation();
    const { AUTO_OPTIMIZE_LABEL, TURN_TYPE_LABEL } = useTranslation();
    const turnTypeIndex = TURN_TYPE_OPTIONS.findIndex(option => option.value === config.turnType);
    const autoOptimizeIndex = config.autoOptimizeAngle
        ? AUTO_OPTIMIZE_ON_INDEX
        : AUTO_OPTIMIZE_OFF_INDEX;
    const update = (partial: Partial<RoutePlanConfig>) => onConfigChange({ config: partial });
    const { handleAltitudeChange, handleRouteSpacingChange } = useImperialHandlers({
        onUpdate: update
    });
    const handleRouteSpacing = (value: number) => handleRouteSpacingChange({ value });
    const handleAltitude = (value: number) => handleAltitudeChange({ value });
    const handlePathAngle = (value: number) => update({ pathAngleDegrees: value });
    const handleTurnType = (value: string) => update({ turnType: value as TurnType });
    const handleAutoOptimize = (value: string) =>
        update({ autoOptimizeAngle: value === AUTO_OPTIMIZE_ON_VALUE });

    return (
        <VerticalContainer
            className={`${className} ${styles.primaryParamsSection}`}
            testId={testId}>
            <SectionEyebrow
                eyebrowText={PRIMARY_PARAMS_EYEBROW_TITLE}
                titleText={PRIMARY_PARAMS_EYEBROW_SUBTITLE}
            />

            <PrimaryParamsSliders
                config={config}
                onAltitudeChange={handleAltitude}
                onPathAngleChange={handlePathAngle}
                onRouteSpacingChange={handleRouteSpacing}
            />

            <LabeledToggle
                activeIndex={turnTypeIndex}
                label={TURN_TYPE_LABEL}
                onClick={handleTurnType}
                options={TURN_TYPE_OPTIONS}
            />

            <LabeledToggle
                activeIndex={autoOptimizeIndex}
                hint={PATH_ANGLE_HINT}
                label={AUTO_OPTIMIZE_LABEL}
                onClick={handleAutoOptimize}
                options={AUTO_OPTIMIZE_OPTIONS}
                variant={VARIANT_SWITCH}
            />
        </VerticalContainer>
    );
};

export default PrimaryParamsSection;
